import { TrackerDataType } from '../constants/constants';
import { useDispatch } from 'react-redux';
import { openSnackbar } from '../../components/AppSnackbar/snackbarSlice';
import { ServiceKeys } from '../constants/IssueAndServiceKeys';
import { htmlToText } from 'html-to-text';
import axios from 'axios';
import dayjs from '../../utils/dayjs';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createService,
  deleteService,
  updateServiceWithFiles,
  uploadServiceFiles,
  uploadServiceUrl,
} from '../requests/serviceNetworkRequests';
import { duplicateServiceLog } from '../../components/TrackerTable/trackerTableRequest';

export const useServiceLogHandler = ({ recentCreations, onSuccessCallback }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const invalidateDropdownTable = async () => {
    await queryClient.invalidateQueries({ queryKey: ['dropdown-table'] });
  };

  const { markCreated, isNewlyAdded } = recentCreations;

  const createServiceMutation = useMutation({
    mutationFn: async ({ fields, files }) => {
      const response = await createService(fields);
      if (response.status !== 201) {
        dispatch(openSnackbar({ severity: 'alert', message: response.statusText }));
        throw new Error(response.statusText);
      }

      const id = response.data.id?.[0] || null;
      if (id) {
        const uploadResp = await uploadServiceFiles({ id, files });
        if (uploadResp.status !== 201) {
          dispatch(openSnackbar({ severity: 'warning', message: 'File upload failed for newly created service' }));
        }

        await Promise.all(
          files
            .filter((file) => file.type === 'url')
            .map((file) =>
              uploadServiceUrl({ id, file }).then((res) => {
                if (res.status !== 201) {
                  dispatch(
                    openSnackbar({ severity: 'warning', message: 'Url upload failed for newly created service' })
                  );
                }
              })
            )
        );
      }

      return { id };
    },
    onError: (error) => {
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        dispatch(openSnackbar({ severity: 'error', message: error.response.data.message }));
      }
    },
    onSuccess: async ({ id }) => {
      await invalidateDropdownTable();
      if (id && markCreated) {
        markCreated(id);
      }
      if (onSuccessCallback) {
        await onSuccessCallback();
      }
    },
  });

  const updateServiceWithFileMutation = useMutation({
    mutationFn: async ({ id, fields, uploadingFiles, uploadingUrls, removingFiles, removingUrls }) => {
      const data = await updateServiceWithFiles({
        id,
        fields,
        uploadingFiles,
        uploadingUrls,
        removingFiles,
        removingUrls,
      });
      return data;
    },
    onError: (err) => {
      console.error(`failed to update service with files - ${err}`);
    },
    onSuccess: async () => {
      await invalidateDropdownTable();
      if (onSuccessCallback) {
        await onSuccessCallback();
      }
    },
  });

  const duplicateServiceMutation = useMutation({
    mutationFn: async (id) => {
      const data = await duplicateServiceLog(id);
      return data;
    },
    onSuccess: async ({ id }) => {
      if (id) {
        markCreated(id);
      }
      if (onSuccessCallback) {
        await onSuccessCallback();
      }
      await invalidateDropdownTable();
    },
  });

  const deleteServiceMutation = useMutation({
    mutationFn: async (id) => {
      const data = await deleteService(id);
      return data;
    },
    onSuccess: async () => {
      await invalidateDropdownTable();
      if (onSuccessCallback) {
        await onSuccessCallback();
      }
    },
  });

  return {
    type: TrackerDataType.SERVICE,
    newItemModalTitle: 'Add New Service',
    createNewItem: async (fields, files, successCallback) => {
      const result = await createServiceMutation.mutateAsync({ fields, files });
      if (successCallback && result?.id) {
        successCallback({ postId: result.id });
      }
    },
    useListenOnCreation: (rowId) => {
      return { isNewlyAdded: isNewlyAdded(rowId) };
    },
    duplicateItem: async (id) => {
      await duplicateServiceMutation.mutateAsync(id);
    },
    editItem: (val, index) => {
      let newVal;
      if (!val) {
        newVal = null;
      } else {
        switch (index) {
          case ServiceKeys.type:
            newVal = [...val];
            break;
          case ServiceKeys.service:
          case ServiceKeys.actions:
          case ServiceKeys.keyMessaging:
          case ServiceKeys.entryDate:
          case ServiceKeys.dueDate:
          case ServiceKeys.unitLead:
            newVal = val;
            break;
          case ServiceKeys.teamMember:
            newVal = val;
            break;
          case ServiceKeys.units:
          case ServiceKeys.campaign:
            let arr = [];
            val.forEach((obj) => arr.push(obj));
            newVal = arr;
            break;
          case ServiceKeys.file:
            newVal = val;
            break;
          // temporarily hidden
          // case ServiceKeys.diversity:
          //   newVal = val._id;
          //   break;
          case ServiceKeys.complexity:
            newVal = val._id;
            break;
          case ServiceKeys.dept:
            newVal = val.map((c) => c.name);
            break;
          default:
            newVal = val.name;
            break;
        }
      }
      return newVal;
    },
    updateItemWithFile: async ({ id, fields }, uploadingFiles, uploadingUrls, removingFiles, removingUrls) => {
      await updateServiceWithFileMutation.mutateAsync({
        id,
        fields,
        uploadingFiles,
        removingFiles,
        uploadingUrls,
        removingUrls,
      });
    },
    deleteItem: async (id) => {
      try {
        const data = await deleteServiceMutation.mutateAsync(id);
        return data;
      } catch (err) {
        console.error(`failed to delete service - ${err}`);
      }
    },
    flattenForExport: (data, dropdownTable) => {
      return data.map((row) => {
        try {
          let flatRow = JSON.parse(JSON.stringify(row.cells));

          delete flatRow[ServiceKeys.date];
          flatRow[ServiceKeys.entryDate] = dayjs(flatRow[ServiceKeys.entryDate]).format('YYYY-MM-DD');
          flatRow[ServiceKeys.dueDate] = flatRow[ServiceKeys.dueDate]
            ? dayjs(flatRow[ServiceKeys.dueDate]).format('YYYY-MM-DD')
            : '';

          flatRow[ServiceKeys.type] = flatRow[ServiceKeys.type].map((c) => `${c.type}:${c.name}`).join(', \n');

          flatRow[ServiceKeys.teamMember] =
            flatRow[ServiceKeys.teamMember].map(
              (tm) => dropdownTable['lead'].find((user) => user['_id'] === tm)?.name
            ) ?? ''.join(', \n');

          flatRow[ServiceKeys.linkedInteraction] = flatRow[ServiceKeys.linkedInteraction]['interactions'].map(
            (c) => `${c.outlet}(Reach ${c.reach})`
          );

          const resolveOptionNames = (items, dropdownKey) => {
            let list = [];
            if (Array.isArray(items)) {
              list = items;
            } else if (items) {
              list = [items];
            }
            const lookup = dropdownTable?.[dropdownKey] ?? [];
            return list
              .map((item) => {
                if (!item) return '';
                if (typeof item === 'string') {
                  return lookup.find((option) => option._id === item)?.name ?? item;
                }
                if (typeof item === 'object') {
                  if (item.name) return item.name;
                  if (item._id) {
                    return lookup.find((option) => option._id === item._id)?.name ?? '';
                  }
                }
                return '';
              })
              .filter(Boolean);
          };

          const linkedEngagementCell = flatRow[ServiceKeys.linkedEngagement];
          const linkedEngagements = linkedEngagementCell?.engagements ?? linkedEngagementCell ?? [];
          flatRow[ServiceKeys.linkedEngagement] = Array.isArray(linkedEngagements)
            ? linkedEngagements
                .map((engagement, index) => {
                  let communityMemberNames = resolveOptionNames(
                    engagement.communityMembers ?? engagement.communityMember,
                    'communityMember'
                  );

                  const label = communityMemberNames
                    .filter((name) => name !== '')
                    .join(', \n')
                    .trim();
                  return label || null;
                })
                .filter(Boolean)
            : [];
          flatRow[ServiceKeys.unitLead] = flatRow[ServiceKeys.unitLead].map((c) => c.name);
          flatRow[ServiceKeys.file] = flatRow[ServiceKeys.file].map((file) => {
            if (file.type === 'url') {
              return file.path;
            } else {
              return file.name;
            }
          });

          flatRow[ServiceKeys.campaign] = flatRow[ServiceKeys.campaign]
            .map((campaign) => `${campaign.name}`)
            .join(', \n');

          let messaging = htmlToText(flatRow[ServiceKeys.keyMessaging], { wordwrap: null });
          messaging = messaging.replace(/"/g, '""');
          flatRow[ServiceKeys.keyMessaging] = messaging;

          let response = htmlToText(flatRow[ServiceKeys.actions], { wordwrap: null });
          response = response.replace(/"/g, '""');
          flatRow[ServiceKeys.actions] = response;

          delete flatRow[ServiceKeys.autotracker];

          return flatRow;
        } catch {
          return {
            [ServiceKeys.entryDate]: '',
            [ServiceKeys.dueDate]: '',
            [ServiceKeys.service]: 'error flattening data for export!',
            [ServiceKeys.type]: '',
            [ServiceKeys.complexity]: '',
            [ServiceKeys.units]: '',
            [ServiceKeys.unitLead]: '',
            [ServiceKeys.teamMember]: '',
            [ServiceKeys.status]: '',
            // temporarily hidden
            // [ServiceKeys.diversity]: '',
            [ServiceKeys.actions]: '',
            [ServiceKeys.keyMessaging]: '',
            [ServiceKeys.linkedInteraction]: '',
            [ServiceKeys.linkedEngagement]: '',
            [ServiceKeys.campaign]: '',
            [ServiceKeys.file]: '',
            [ServiceKeys.dept]: '',
          };
        }
      });
    },
  };
};
